import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Workflow as WorkflowIcon, Plus, Play, Trash2, Loader2, CheckCircle2, XCircle,
  Zap,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P7 — Workflow Builder (form-based; no drag&drop to keep a clean surface)
 * URL: /admin/automation/workflows
 *
 * Create a Workflow with: name, trigger (event + conditions[]), actions[].
 */

const ACTION_KINDS = [
  { value: 'create_task', label: 'Create Task' },
  { value: 'create_notification', label: 'Create Notification' },
  { value: 'create_alert', label: 'Create Risk Alert' },
  { value: 'send_email', label: 'Send Email' },
  { value: 'webhook', label: 'Trigger Webhook' },
];

const DEFAULT_PARAMS = {
  create_task: { title: '', description: '', priority: 'medium' },
  create_notification: { channel: 'in_app', subject: '', body: '', link: '' },
  create_alert: { kind: 'sanctions_match', severity: 'warning', title: '', message: '' },
  send_email: { to: '', subject: '', body: '' },
  webhook: { url: '' },
};


const WorkflowsList = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [workflows, setWorkflows] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyFire, setBusyFire] = useState(null);

  const [form, setForm] = useState({
    name: '', description: '', enabled: true,
    trigger: { event: '', conditions: [] },
    actions: [{ kind: 'create_task', params: { ...DEFAULT_PARAMS.create_task } }],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, e] = await Promise.all([
        axios.get(`${API_URL}/api/automation/workflows`, authHeader),
        axios.get(`${API_URL}/api/automation/events/catalog`, authHeader),
      ]);
      setWorkflows(w.data || []);
      setEvents(e.data || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authHeader, toast]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = useCallback(async (wf) => {
    try {
      await axios.patch(`${API_URL}/api/automation/workflows/${wf.id}`, { enabled: !wf.enabled }, authHeader);
      await load();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast, load]);

  const deleteWf = useCallback(async (id) => {
    if (!window.confirm('Delete this workflow?')) return; // eslint-disable-line no-alert
    try {
      await axios.delete(`${API_URL}/api/automation/workflows/${id}`, authHeader);
      toast({ title: 'Workflow deleted' });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast, load]);

  const fireTest = useCallback(async (wf) => {
    setBusyFire(wf.id);
    try {
      await axios.post(`${API_URL}/api/automation/events/emit`, {
        event: wf.trigger.event,
        payload: { test: true, po_number: 'TEST-0001', title: 'Test Event', supplier_name: 'ACME', end_date: '2099-12-31', counterparty_name: 'Test Counterparty' },
      }, authHeader);
      toast({ title: 'Event fired', description: `${wf.trigger.event} dispatched` });
      await load();
    } catch (err) {
      toast({ title: 'Fire failed', description: err?.message, variant: 'destructive' });
    } finally { setBusyFire(null); }
  }, [authHeader, toast, load]);

  const addAction = () => setForm((f) => ({
    ...f, actions: [...f.actions, { kind: 'create_task', params: { ...DEFAULT_PARAMS.create_task } }],
  }));

  const removeAction = (idx) => setForm((f) => ({
    ...f, actions: f.actions.filter((_, i) => i !== idx),
  }));

  const changeActionKind = (idx, kind) => setForm((f) => ({
    ...f,
    actions: f.actions.map((a, i) => i === idx ? { kind, params: { ...DEFAULT_PARAMS[kind] } } : a),
  }));

  const changeActionParam = (idx, key, value) => setForm((f) => ({
    ...f,
    actions: f.actions.map((a, i) => i === idx ? { ...a, params: { ...a.params, [key]: value } } : a),
  }));

  const createWorkflow = useCallback(async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/automation/workflows`, form, authHeader);
      toast({ title: 'Workflow created' });
      setDialogOpen(false);
      setForm({
        name: '', description: '', enabled: true,
        trigger: { event: events[0] || '', conditions: [] },
        actions: [{ kind: 'create_task', params: { ...DEFAULT_PARAMS.create_task } }],
      });
      await load();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setCreating(false); }
  }, [form, authHeader, toast, load, events]);

  return (
    <div data-testid="workflows-list-page">
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
            <WorkflowIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#2B2D42]">Workflow Builder</h1>
            <p className="text-sm text-[#6C757D]">Define automations: trigger → conditions → actions.</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="workflows-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Workflow</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="workflows-new-dialog">
            <DialogHeader>
              <DialogTitle>New Workflow</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="workflow-form-name" /></Field>
              <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="workflow-form-description" /></Field>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-[10px] uppercase font-bold text-[#2B2D42]">Trigger</Label>
                <div className="mt-2 grid grid-cols-1 gap-2">
                  <Field label="Event">
                    <select
                      value={form.trigger.event}
                      onChange={(e) => setForm({ ...form, trigger: { ...form.trigger, event: e.target.value } })}
                      data-testid="workflow-form-event"
                      className="text-sm w-full border border-slate-300 rounded px-2 py-1 bg-white">
                      <option value="">— Select event —</option>
                      {events.map((ev) => (<option key={ev} value={ev}>{ev}</option>))}
                    </select>
                  </Field>
                  <Field label="Conditions (one per line, e.g. payload.total_amount > 10000)">
                    <Textarea rows={2}
                      value={(form.trigger.conditions || []).join('\n')}
                      onChange={(e) => setForm({
                        ...form,
                        trigger: { ...form.trigger, conditions: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) },
                      })}
                      data-testid="workflow-form-conditions"
                      placeholder="payload.total_amount > 10000" />
                  </Field>
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[10px] uppercase font-bold text-[#2B2D42]">Actions</Label>
                  <Button size="sm" variant="outline" onClick={addAction} data-testid="workflow-form-add-action">+ Add action</Button>
                </div>
                <div className="space-y-3">
                  {form.actions.map((a, idx) => (
                    <div key={idx} className="p-2 bg-white rounded border border-slate-200" data-testid={`workflow-form-action-${idx}`}>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <select
                          value={a.kind}
                          onChange={(e) => changeActionKind(idx, e.target.value)}
                          data-testid={`workflow-form-action-kind-${idx}`}
                          className="text-xs border border-slate-300 rounded px-2 py-1 bg-white flex-1">
                          {ACTION_KINDS.map((k) => (<option key={k.value} value={k.value}>{k.label}</option>))}
                        </select>
                        <Button size="sm" variant="ghost" onClick={() => removeAction(idx)} data-testid={`workflow-form-action-remove-${idx}`} className="text-rose-600 h-7 px-2">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(a.params).map(([k, v]) => (
                          <Field key={k} label={k}>
                            <Input value={String(v || '')} onChange={(e) => changeActionParam(idx, k, e.target.value)} data-testid={`workflow-form-action-${idx}-${k}`} />
                          </Field>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={createWorkflow} disabled={creating || !form.name || !form.trigger.event} data-testid="workflow-form-submit">
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="workflows-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : workflows.length === 0 ? (
            <div className="p-6 text-center text-xs text-[#6C757D]" data-testid="workflows-empty">No workflows yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="workflows-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase text-[#6C757D]">
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Trigger</th>
                    <th className="px-3 py-2 text-center">Actions</th>
                    <th className="px-3 py-2 text-center">Runs</th>
                    <th className="px-3 py-2 text-center">Success</th>
                    <th className="px-3 py-2 text-center">Enabled</th>
                    <th className="px-3 py-2 text-right">Ops</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {workflows.map((w) => (
                    <tr key={w.id} data-testid={`workflow-row-${w.id}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="text-sm font-semibold text-[#2B2D42]">{w.name}</div>
                        <div className="text-[10px] text-[#6C757D] max-w-sm truncate">{w.description}</div>
                      </td>
                      <td className="px-3 py-2"><code className="text-xs text-[#023E8A]">{w.trigger?.event}</code></td>
                      <td className="px-3 py-2 text-center text-xs">{(w.actions || []).length}</td>
                      <td className="px-3 py-2 text-center text-xs font-mono">{w.stats?.runs || 0}</td>
                      <td className="px-3 py-2 text-center">
                        <span className="inline-flex items-center gap-1 text-xs">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />{w.stats?.successes || 0}
                          <XCircle className="w-3 h-3 text-rose-600 ml-1" />{w.stats?.failures || 0}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Switch checked={!!w.enabled} onCheckedChange={() => toggleEnabled(w)} data-testid={`workflow-toggle-${w.id}`} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => fireTest(w)} disabled={busyFire === w.id} data-testid={`workflow-fire-${w.id}`} title="Fire test event">
                          {busyFire === w.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5 text-[#FF6B35]" />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteWf(w.id)} data-testid={`workflow-delete-${w.id}`}>
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const Field = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase font-semibold text-[#6C757D] block mb-1">{label}</Label>
    {children}
  </div>
);


export default WorkflowsList;
